import { html, css, nothing } from "lit";
import type { TemplateResult } from "lit";
import { customElement, state } from "lit/decorators.js";
import { localized, msg } from "@lit/localize";

import "@wh-web-front-end/wh-component-library/dist/elements/wh-icon";
import "../../elements/web-kiosk-device-attribute";
import type {
  ConnectedDevicesContent,
  ConnectedDeviceAttribute,
} from "../../types/content";
import { WebKioskPageElement } from "../web-kiosk-page";

declare global {
  interface HTMLElementTagNameMap {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    "web-kiosk-connected-device": WebKioskConnectedDeviceElement;
  }
}

@localized()
@customElement("web-kiosk-connected-device")
export class WebKioskConnectedDeviceElement extends WebKioskPageElement {
  @state() content: ConnectedDevicesContent | undefined = undefined;

  @state() private device: string | null | undefined = undefined;

  firstUpdated(): void {
    // eslint-disable-next-line total-functions/no-partial-url-constructor
    const url = new URL(window.location.toString()).searchParams;
    this.device = url.get("device");

    this.updateTitle();
  }

  updateTitle(): void {
    const deviceContent = this.content?.devices.find(
      (deviceObject) => deviceObject.id === this.device
    );

    if (deviceContent) {
      this.setTitle(deviceContent.title);
    }
  }

  renderAttributes(attributes: ConnectedDeviceAttribute[]) {
    return attributes.map((attribute) => {
      const { title, body: attributeBody, icon } = attribute;
      return html`
        <web-kiosk-device-attribute
          slot="attributes"
          heading=${title}
          body=${attributeBody}
        >
          <img src=${icon} alt=${title} slot="icon"
        /></web-kiosk-device-attribute>
      `;
    });
  }

  renderDisclaimer(disclaimer: string) {
    if (!disclaimer) {
      return nothing;
    }

    return html`<p class="disclaimer">${disclaimer}</p>`;
  }

  render(): TemplateResult | typeof nothing {
    if (!this.content) {
      return nothing;
    }
    const deviceContent = this.content.devices.find(
      (deviceObject) => deviceObject.id === this.device
    );

    if (!deviceContent) {
      return html`<p>Device ${this.device} not found.</p>`;
    }

    const { name, body, img, attributes, disclaimer } = deviceContent;

    return html`<section id="device">
      <div id="navigation">
        <a href=${this.createRelativeLink("connectedDevices")}>
          <wh-icon name="chevron-down"></wh-icon>
          ${msg("Back to Device List", { id: "device-back" })}
        </a>
      </div>
      <div id="left-panel">
        <h1>${name}</h1>
        <p class="subtitle">${body}</p>
        <img src=${img} alt=${name} class="device-image" />
      </div>
      <div id="right-panel">
        <section id="attributes">${this.renderAttributes(attributes)}</section>
        ${this.renderDisclaimer(disclaimer)}
      </div>
    </section>`;
  }

  static styles = css`
    :host {
      --web-kiosk-bg-blue: #c1e8ff;
      --web-kiosk-bg-grey: #f5f5f0;
      --web-kiosk-link-color: #284b9b;
      --web-kiosk-text-color: #323264;
      --web-kiosk-disclaimer-color: black;
      --web-kiosk-panel-padding-m: 64px;
      --web-kiosk-panel-padding-sm: 56px;
    }

    #navigation {
      display: flex;
      padding: 52px 58px 48px 56px;
      color: var(--web-kiosk-link-color, hotpink);
      font-size: 32px;
    }

    #navigation a {
      width: 20%;
      display: flex;
      align-items: center;
      color: var(--web-kiosk-link-color, hotpink);
      text-decoration: none;
      font-weight: bold;
    }

    #navigation wh-icon {
      font-size: 50px;
      transform: rotate(90deg);
    }

    /* stylelint-disable indentation */
    #device {
      height: 100%;
      background: url("assets/about/bg/ribbon-health-advisors.svg") right 0 top
          0 no-repeat,
        linear-gradient(
          90deg,
          var(--web-kiosk-bg-blue, #c1e8ff) 42%,
          var(--web-kiosk-bg-grey, #f5f5f0) 0
        );
      background-size: 58%, 100%;
    }
    /* stylelint-enable indentation */

    #left-panel {
      float: left;
      width: 42%;
      box-sizing: border-box;
      padding: 0 var(--web-kiosk-panel-padding-m, 64px) 0
        var(--web-kiosk-panel-padding-sm, 56px);
    }

    #right-panel {
      float: right;
      width: 58%;
      box-sizing: border-box;
      padding: 0 90px;
      margin-top: 28px;
    }

    #attributes {
      padding: 0 51px;
    }

    h1 {
      color: var(--web-kiosk-text-color, hotpink);
      font-size: 48px;
      font-weight: 700;
      line-height: 62px;
    }

    p {
      color: var(--web-kiosk-text-color, hotpink);
      font-size: 26px;
      padding-bottom: 40px;
      line-height: 40px;
      font-weight: 400;
    }

    p.subtitle {
      color: var(--web-kiosk-text-color, hotpink);
      font-size: 32px;
      line-height: 52px;
      font-weight: 400;
    }

    p.disclaimer {
      color: var(--web-kiosk-disclaimer-color, hotpink);
      font-size: 20px;
      line-height: 28px;
      font-weight: 400;
    }

    .device-image {
      max-width: 100%;
    }
  `;
}
